import React, { useState, useEffect } from "react";
import {
  ImageInput,
  useDataProvider,
  useRecordContext,
  useNotify
} from "react-admin";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Modal,
  Typography,
  useTheme
} from "@mui/material";
import "react-image-crop/dist/ReactCrop.css";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MultipartUpload from "src/providers/MultipartUpload";
import ApiResources from "src/providers/ApiResources";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FileUploader } from "react-drag-drop-files";

export interface ImageSize {
  name: string;
  option: string;
  resolution: [number, number];
}

export const GalleryItemModal = (props) => {
  const { open, setOpen, sortOrder, handleClose } = props;

  const rec = useRecordContext();
  const [record, setRecord] = useState(rec);
  const [uploads, setUploads] = useState([]);
  const dataProvider = useDataProvider();
  const [uploading, setUploading] = useState(false);
  const notify = useNotify();
  const fileTypes = ["jpg", "jpeg", "png", "gif", "svg", "svgz"];
  const theme = useTheme();

  const handleChange = (fileList: FileList) => {
    let newUploads = [...uploads];
    for (let i = 0; i < fileList.length; i++) {

      var binaryData = [];
      binaryData.push(fileList.item(i));
      const file = URL.createObjectURL(new Blob(binaryData));

      newUploads.push({
        name: fileList.item(i).name,
        uploaded: false,
        preview: file,
        file: fileList.item(i),
      });
    }
    setUploads(newUploads);
  };

  function transformName(filename) {
    return filename.replace(/[^a-zA-Z0-9]+(?=\.[^.]+$)|[^a-zA-Z0-9.]/g, "-");
  }

  const removeFromUploads = (name: string) => {
    let newUploads = [...uploads].filter((upload) => upload.name !== name);
    setUploads(newUploads);
  };

  const handleUpload = async () => {
    setUploading(true);
    for (let i = 0; i < uploads.length; i++) {
      await uploadFile(i);
    }
    setUploading(false);
  };

  async function uploadFile(i: number) {
    if (uploads[i].uploaded) return;

    const data = new FormData();
    let filename = transformName(uploads[i].name);

    data.append("images[]", uploads[i].file);

    await MultipartUpload(
      ApiResources.postProductImage(record.id),
      data
    )
      .then((res) => res.json())
      .then(async (res) => {
        let sortOrd = sortOrder;

        let newGalleryItem = {
          product: record.id,
          name: filename,
          image: res[0].file,
          featured: "false",
          sortOrder: sortOrd ? sortOrd : 0,
        };

        let newUploads = [...uploads];
        newUploads[i].uploaded = true;
        newUploads[i].path = res[0].file;
        setUploads(newUploads);

        await uploadGalleryItem(newGalleryItem);
      });
  }

  function isDone() {
    let progress = uploads.filter(
      (image) => image.uploaded !== true
    );

    return progress.length == 0 ? true : false;
  }

  async function uploadGalleryItem(newGalleryItem) {
    await dataProvider
      .create(ApiResources.postProductGallery(false), { data: newGalleryItem })
      .then(({ data }) => {
      })
      .catch(error => {
        notify(error.message, { type: 'warning' });
      });
  }

  function handleDone() {
    setUploads([]);
    handleClose();
  }

  const boxStyle = {
    display: "flex",
    justifyContent: "center",
    color: theme.palette.grey["400"],
    padding: "25px",
    backgroundColor: theme.palette.grey["100"],
    borderRadius: 1,
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "Segoe UI",
    cursor: "pointer"
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box sx={modalContentStyle}>
        <Box sx={tabBoxStyle}>
          <Box sx={{ p: 4 }}>
            <Typography
              component="span"
              fontSize="0.75rem"
              color="text.secondary"
              marginBottom="5px"
              display="block"
            >
              {`Upload Image 10MB max`}
            </Typography>
            <FileUploader
              types={fileTypes}
              multiple={true}
              handleChange={(fileList: FileList) => handleChange(fileList)}
              name="files"
            >
            <Box sx={boxStyle}>
              <Box display="flex" alignItems="center" gap="7px">
                <Box>{`Drop some pictures to upload, or click to select one.`}</Box>
              </Box>
            </Box>
          </FileUploader>

          </Box>
          {uploads.length > 0 && (
            <>
              <Grid container>
                <Grid item xs={12}>
                  <List
                    dense={false}
                    sx={{
                      height: "300px",
                      overflowY: "auto",
                      paddingX: 4,
                    }}
                  >
                    {uploads.map((image, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <Box sx={{ display: "block", textAlign: "center" }}>
                            {image.uploaded ? <CheckCircleIcon /> : null}
                          </Box>
                        }
                        sx={{
                          ".MuiListItemSecondaryAction-root": {
                            marginRight: "5%",
                          },
                          ".MuiListItemText-primary, .MuiListItemText-root": {
                            marginRight: 0,
                          },
                          padding: "7.5px",
                          marginY: "5px",
                          borderRadius: "5px",
                          backgroundColor: "#f7f8f8",
                          ":hover": {
                            backgroundColor: "#a8a8a8",
                          },
                        }}
                      >
                        <Box
                          style={{
                            minWidth: "36px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <DeleteIcon
                            sx={{ color: "#00509f", cursor: "pointer" }}
                            onClick={() => removeFromUploads(image.name)}
                          />
                        </Box>
                        <ListItemAvatar
                          style={{
                            minWidth: "150px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            className="preview"
                            src={image.preview}
                            alt=""
                            style={{
                              width: "80px",
                              height: "60px",
                              borderRadius: "5px",
                              objectFit: "cover",
                            }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body1"
                                fontWeight="bold"
                                color="text.primary"
                                textAlign="left"
                              >
                                {image.name}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
        <Box sx={footerStyle}>
          { uploading &&
            <CircularProgress size={30} thickness={2} sx={{ fontSize: "28px", color: "#1976d2", marginRight: "8px", alignSelf: "center"}}/>
          }
          {!isDone() || uploads.length === 0 ? (
            <>
            <Button
              variant="contained"
              disabled={uploads.length === 0}
              startIcon={<FileUploadIcon />}
              onClick={() => handleUpload()}
            >
              Upload
            </Button>
            <Button variant="outlined" onClick={() => handleDone()}>
              Cancel
            </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => handleDone()}>
              Done
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  columnGap: "10px",
  paddingRight: 4,
  paddingTop: "20px",
};

const tabBoxStyle = {
  width: "100%",
  height: "100%",
  minWidth: "700px",
  borderBottom: 1,
  borderColor: "divider",
  overflow: "auto",
};

const modalContentStyle = {
  position: "absolute" as "absolute",
  top: "25%",
  left: "50%",
  transform: "translate(-50%, -25%)",
  bgcolor: "white",
  boxShadow: 24,
  paddingY: 4,
  paddingX: 0,
  overflow: "auto",
};

const typesStyle = {
  width: "40%",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
};
